import { Channel, Presence, Socket } from "phoenix";

import { __GLOBAL_CLIENT_ID } from "@/client/globals";
import { WS_ENDPOINT } from "@/config";
import { LocalCollabPresence } from "@/features/authority";

/// SINGLETON Global User Socket.
let socket: Socket | null = null;

/// SINGLETON Global Student channels record per topic.
const channels: Record<string, StudentChannel> = {};

/// A Channel and its Presence for a given Student ID.
export interface StudentChannel {
  topic: string;
  channel: Channel;
  presence: Presence;
  collabPresence: LocalCollabPresence;
}

/// Connection params for Socket and StudentChannel.
export type ConnectParams =
  | (ConnectByUser & ConnectByWork)
  | (ConnectByUser & ConnectByGroup);

interface ConnectByUser {
  userToken: string;
  tenant: string;
}

interface ConnectByWork {
  channelType: "work";
  workId: string;
}

interface ConnectByGroup {
  channelType: "group";
  groupId: string;
}

/** Connect to the Phoenix User Socket and join a Student Channel. */
export function connectStudentChannel(params: ConnectParams) {
  const socket = connectSocket(params.userToken);
  const topic = makeTopic(params);
  joinChannel(socket, topic, params.tenant);
}

function makeTopic(params: ConnectByWork | ConnectByGroup): string {
  switch (params.channelType) {
    case "work":
      return `student:work:${params.workId}`;
    case "group":
      return `student:group:${params.groupId}`;
  }
}

/**
 * Access the Student Channel and its Presence using either Work ID or Group ID.
 */
export function getStudentChannel(
  params: ConnectByWork | ConnectByGroup
): StudentChannel | null {
  const topic = makeTopic(params);
  const studentChannel = channels[topic];
  if (!studentChannel) {
    return null;
  }
  return studentChannel;
}

// Connect to the Phoenix User Socket with the given user token.
function connectSocket(userToken: string): Socket {
  if (socket) {
    return socket;
  }
  socket = new Socket(WS_ENDPOINT, {
    params: {
      token: userToken,
    },
  });
  socket.connect();
  socket.onClose((...args) => {
    console.info("Socket closed", args);
  });
  return socket;
}

// Join a Student Channel on the Socket.
function joinChannel(
  socket: Socket,
  topic: string,
  tenant: string
): StudentChannel {
  if (channels[topic]) {
    return channels[topic];
  }

  const channel = socket.channel(topic, {
    tenant: tenant,
    client_id: __GLOBAL_CLIENT_ID.current,
  });

  channel
    .join()
    .receive("error", (reason) =>
      console.error(`Failed to join channel: ${reason}`)
    );

  const presence = new Presence(channel);
  const collabPresence = new LocalCollabPresence(presence);
  collabPresence.startSync();

  const studentChannel = {
    topic,
    channel,
    presence,
    collabPresence,
  };
  channels[topic] = studentChannel;
  return studentChannel;
}
